import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { ChevronRight } from "lucide-solid";
import { type Component, createEffect } from "solid-js";
import { cn, type ListWithMeta } from "@listto/shared";
import type { Todo } from "@listto/powersync";
import { TodoItem } from "./todo-item";
import type { VirtualRow } from "../types";
import { type FocusSlide, playFocusSlideAnimation } from "../hooks/use-drawer-swipe-nav";

interface RowItemProps {
  row: VirtualRow;
  index: number;
  isSelectionMode: boolean;
  selectedIds: Set<string>;
  exitingIds: Set<string>;
  newlyAddedIds: Set<string>;
  onToggleSelection: (id: string) => void;
  listId: string;
  lists: ListWithMeta[] | undefined;
  isInitialLoad: boolean;
  onToggle: (todo: Todo) => void;
  onDelete: (id: string) => void;
  onSelect: (id: string) => void;
  focusedTodoId?: string;
  onFocus?: (id: string) => void;
  focusSlide: FocusSlide | null;
}

export const PendingRowItem: Component<RowItemProps> = (props) => {
  const todo = () => {
    const r = props.row;
    if (r.kind !== "pending") {
      throw new Error("Invalid row kind for PendingRowItem");
    }
    return r.todo;
  };

  return (
    <TodoItem
      todo={todo()}
      onToggle={props.onToggle}
      onDelete={props.onDelete}
      onSelect={props.onSelect}
      isSelectionMode={props.isSelectionMode}
      isSelected={props.selectedIds.has(todo().id)}
      onToggleSelection={props.onToggleSelection}
      listId={props.listId}
      lists={props.lists}
      isInitialLoad={props.isInitialLoad}
      index={props.index}
      isExiting={props.exitingIds.has(todo().id)}
      isNew={props.newlyAddedIds.has(todo().id)}
      isKeyboardFocused={props.focusedTodoId === todo().id}
      onFocus={props.onFocus}
      focusSlide={props.focusedTodoId === todo().id ? props.focusSlide : null}
    />
  );
};

export const CompletedRowItem: Component<RowItemProps> = (props) => {
  const todo = () => {
    const r = props.row;
    if (r.kind !== "completed") {
      throw new Error("Invalid row kind for CompletedRowItem");
    }
    return r.todo;
  };

  return (
    <div class="animate-completed-reveal will-change-[transform,opacity]">
      <TodoItem
        todo={todo()}
        onToggle={props.onToggle}
        onDelete={props.onDelete}
        onSelect={props.onSelect}
        isSelectionMode={props.isSelectionMode}
        isSelected={props.selectedIds.has(todo().id)}
        onToggleSelection={props.onToggleSelection}
        listId={props.listId}
        lists={props.lists}
        isInitialLoad={props.isInitialLoad}
        index={props.index}
        isExiting={props.exitingIds.has(todo().id)}
        isNew={props.newlyAddedIds.has(todo().id)}
        isKeyboardFocused={props.focusedTodoId === todo().id}
        onFocus={props.onFocus}
        focusSlide={props.focusedTodoId === todo().id ? props.focusSlide : null}
      />
    </div>
  );
};

interface CompletedHeaderRowItemProps {
  row: VirtualRow;
  index: number;
  isKeyboardFocused: boolean;
  completedCollapsed: boolean;
  onToggleCollapse: () => void;
  t: (message: string | MessageDescriptor) => string;
  onFocus?: (index: number) => void;
  focusSlide: FocusSlide | null;
}

export const CompletedHeaderRowItem: Component<CompletedHeaderRowItemProps> = (props) => {
  const count = () => {
    const r = props.row;
    if (r.kind !== "completed-header") {
      throw new Error("Invalid row kind for CompletedHeaderRowItem");
    }
    return r.count;
  };

  let buttonRef: HTMLButtonElement | undefined = undefined;
  let lastPlayedGeneration = 0;

  createEffect(() => {
    const slide = props.focusSlide;
    if (!slide || !props.isKeyboardFocused) {
      return;
    }
    if (slide.generation === lastPlayedGeneration) {
      return;
    }
    lastPlayedGeneration = slide.generation;
    playFocusSlideAnimation(buttonRef, slide.direction);
  });

  return (
    <button
      id={`completed-header-${props.index}`}
      type="button"
      ref={(el) => {
        buttonRef = el;
      }}
      class={cn(
        "flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-bold text-muted-foreground/60 hover:text-foreground transition-all duration-200 group w-full cursor-pointer border border-transparent outline-none min-h-[44px]",
        props.isKeyboardFocused &&
          "ring-2 ring-primary bg-primary/5 border-primary shadow-md scale-[1.005] text-foreground",
      )}
      onClick={() => {
        props.onToggleCollapse();
      }}
      onFocus={() => {
        props.onFocus?.(props.index);
      }}
    >
      <ChevronRight
        class={cn(
          "h-4 w-4 transition-transform duration-300",
          !props.completedCollapsed && "rotate-90",
        )}
      />
      {props.t(msg`Completed`)} ({count()})
    </button>
  );
};
